import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ChatActionType } from '@trinity/chat';
import { useSelectedUserTerritoryId } from '@trinity/chat-components/components/Chat.Filters/hooks/useSelectedUserTerritoryId';
import { SubmitActionContext } from './context';
import { HcpSearchFormMessage, type HcpSearchForm, type HcpSelectOption } from './KolSearch.Form';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  disabled?: boolean;
};

export const HcpSearchResponse = ({ disabled = false }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const territoryId = useSelectedUserTerritoryId();
  const [formState, setFormState] = useState<Partial<HcpSearchForm>>({
    hcpId: null,
    name: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleFormUpdated = useCallback((form: Partial<HcpSearchForm>) => {
    setFormState((prev) => ({ ...prev, ...form }));
    setErrorMessage(null);
  }, []);

  const handleAutocompleteSelection = useCallback(
    (item: HcpSelectOption) => {
      if (disabled) {
        return;
      }

      submitAction(
        {
          type: ChatActionType.SelectHcpSubject,
          payload: {
            hcpId: item.id,
            hcpName: item.name,
          },
        },
        `Show me the profile for ${item.name}.`
      );
    },
    [disabled, submitAction]
  );

  const handleLocationSelection = useCallback(
    (location: NonNullable<HcpSearchForm['location']>) => {
      if (disabled) {
        return;
      }

      const trimmedName = formState.name?.trim();

      submitAction(
        {
          type: ChatActionType.SearchHcps,
          payload: {
            ...(trimmedName ? { name: trimmedName } : {}),
            territoryId,
            location: {
              id: location.id,
              latitude: location.latitude,
              longitude: location.longitude,
              name: location.name,
            },
          },
        },
        location.name
          ? `Search for HCPs near ${location.name}.`
          : 'Search for HCPs near the selected location.'
      );
    },
    [disabled, formState, submitAction, territoryId]
  );

  const submitQuery = useMemo(() => {
    const trimmedName = formState.name?.trim();
    if (trimmedName) {
      return `Search for HCPs matching "${trimmedName}".`;
    }

    if (formState.location?.name) {
      return `Search for HCPs near ${formState.location.name}.`;
    }

    return 'Search for HCPs.';
  }, [formState.location?.name, formState.name]);

  const handleSubmit = useCallback(() => {
    if (disabled) {
      return;
    }

    const hcpId = formState.hcpId;
    const hcpName = formState.name?.trim();

    const hasHcpId = typeof hcpId === 'number' || (typeof hcpId === 'string' && hcpId.length > 0);
    if (hasHcpId && hcpName) {
      submitAction(
        {
          type: ChatActionType.SelectHcpSubject,
          payload: {
            hcpId,
            hcpName,
          },
        },
        `Show me the profile for ${hcpName}.`
      );
      return;
    }

    const hasName = !!hcpName;
    const location = formState.location;
    const hasLocation = !!location;

    if (!hasName && !hasLocation) {
      setErrorMessage('Please select an HCP, enter a name, or provide a location before submitting.');
      return;
    }

    submitAction(
      {
        type: ChatActionType.SearchHcps,
        payload: {
          ...(hasName ? { name: hcpName } : {}),
          territoryId,
          ...(hasLocation
            ? {
                location: {
                  id: location.id,
                  latitude: location.latitude,
                  longitude: location.longitude,
                  name: location.name,
                },
              }
            : {}),
        },
      },
      submitQuery
    );
  }, [disabled, formState.hcpId, formState.location, formState.name, submitAction, submitQuery, territoryId]);

  return (
    <MessageContainer>
      <HcpSearchFormMessage
        disabled={disabled}
        formState={formState}
        onAutocompleteSelect={handleAutocompleteSelection}
        onLocationSelect={handleLocationSelection}
        onSubmit={handleSubmit}
        setFormState={handleFormUpdated}
        showSearchButton={false}
      />
      {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
    </MessageContainer>
  );
};

const ErrorMessage = styled.div(({ theme }) => ({
  marginTop: 10,
  color: theme.palette.error.main,
  fontFamily: theme.fonts.regular,
}));
